import React, { use } from "react";
import { useMutation } from "@apollo/client/react";
import VStack from "../../ui_atoms/VStack";
import { useI18n } from "../../hooks/i18n-hooks";
import { AuthManagerContext } from "../../contexts/AuthManager";
import MarkdownView from "../../components/MarkdownView";
import { useTheme } from "../../hooks/use-theme";
import Gap from "../../ui_atoms/Gap";
import { RESEND_VERIFICATION_EMAIL } from "../../graphql/home";
import Button from "../../components/Button";
import ResendVerificationImage from "../../assets/images/resend_verification.svg";
import Callout from "../../ui_atoms/Callout";
import { ScrollView } from "react-native";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";

type ResendEmailVerificationScreenProps = StackScreenProps<
    RootStackParamList,
    "ResendEmailVerification"
>;

const ResendEmailVerificationScreen = ({ route }: ResendEmailVerificationScreenProps) => {
    const { user } = use(AuthManagerContext);
    const { gridSize } = useTheme();
    const i18n = useI18n();
    const [emailSent, setEmailSent] = React.useState(false);
    const [resendVerificationEmail, { loading: saving, error: saveError }] =
        useMutation(RESEND_VERIFICATION_EMAIL);

    const onResend = React.useCallback(() => {
        void resendVerificationEmail({
            variables: {
                clientProfile: !!route.params?.clientProfile,
            },
        });
        setEmailSent(true);
    }, [resendVerificationEmail, route.params?.clientProfile]);

    return (
        <ScrollView>
            <VStack style={{ padding: gridSize }} gap={0.5} alignment="stretch">
                <ResendVerificationImage />
                <MarkdownView
                    style={{ paddingHorizontal: gridSize }}
                    markdown={i18n.t("screens.user.resend_verification_email.text")}
                />
                <Gap gap={0.5} />

                <Button
                    label={i18n.t("screens.user.resend_verification_email.button_text")}
                    size="large"
                    processing={saving}
                    disabled={emailSent}
                    onPress={onResend}
                />
                <Gap gap={0.5} />
                {saveError && (
                    <Callout
                        mode="error"
                        markdown={i18n.t("screens.user.resend_verification_email.error_text")}
                    />
                )}
                {emailSent && (
                    <Callout
                        mode="info"
                        markdown={i18n.t("screens.user.resend_verification_email.sent_text", {
                            email: user?.email,
                        })}
                    />
                )}
            </VStack>
        </ScrollView>
    );
};

export default ResendEmailVerificationScreen;
